<template>
  <span class="brand-name" :class="[toneClass, { 'brand-name--centered': align === 'center', 'brand-name--stack': stack && !hasCustomName2, 'brand-name--fit': hasCustomName2 }]" v-bind="attrs" role="img" :aria-label="`${COMPANY_NAME_1}${currentWord}`">
    <span class="brand-name-1">{{ COMPANY_NAME_1 }}</span><template v-if="rotate">
      <!-- Rotating: sizer holds max-width, visible word overlays absolutely -->
      <span class="brand-name-2-container">
        <span class="brand-name-2 brand-name-2-sizer" aria-hidden="true">{{ longestWord }}</span>
        <transition name="word-fade" mode="out-in">
          <span :key="currentWord" class="brand-name-2 brand-name-2-visible">{{ currentWord }}</span>
        </transition>
      </span>
    </template><template v-else>
      <!-- Static: plain inline span, sizes to actual content -->
      <span class="brand-name-2">{{ currentWord }}</span>
    </template>
  </span>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, useAttrs } from 'vue'
import { COMPANY_NAME_1, COMPANY_NAME_2, COMPANY_NAME_2_WORDS } from '@/config/branding'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  // tone="primary"    (default) — for light/white backgrounds: name1 in primary red, name2 in dark
  // tone="light"               — for dark/black backgrounds: name1 in light-red, name2 in white
  // tone="on-primary"          — for red/primary backgrounds: both parts in white
  tone: {
    type: String,
    default: 'primary'
  },
  // rotate=true — cycle through COMPANY_NAME_2_WORDS; false shows static COMPANY_NAME_2
  rotate: {
    type: Boolean,
    default: false
  },
  // align="center" — block display, centered in parent; "left" (default) is inline-block
  align: {
    type: String,
    default: 'left'
  },
  // stack=true — on narrow screens, wraps name2 below name1 instead of overflowing
  stack: {
    type: Boolean,
    default: false
  },
  // name2Override — replaces COMPANY_NAME_2 with a custom string (disables rotation)
  name2Override: {
    type: String,
    default: null
  }
})

const attrs = useAttrs()

const hasCustomName2 = computed(() => !!props.name2Override)

const toneClass = computed(() => {
  if (props.tone === 'light') return 'tone-light'
  if (props.tone === 'on-primary') return 'tone-on-primary'
  if (props.tone === 'hero') return 'tone-hero'
  return 'tone-primary'
})

const longestWord = computed(() => {
  if (props.name2Override) return props.name2Override
  return COMPANY_NAME_2_WORDS.reduce((a, b) => a.length >= b.length ? a : b, COMPANY_NAME_2)
})

const wordIndex = ref(0)
const currentWord = computed(() => {
  if (props.name2Override) return props.name2Override
  return props.rotate ? (COMPANY_NAME_2_WORDS[wordIndex.value] ?? COMPANY_NAME_2) : COMPANY_NAME_2
})

let intervalId = null

onMounted(() => {
  if (props.rotate) {
    intervalId = setInterval(() => {
      wordIndex.value = (wordIndex.value + 1) % COMPANY_NAME_2_WORDS.length
    }, 3000)
  }
})

onUnmounted(() => {
  clearInterval(intervalId)
})
</script>

<style scoped>
.brand-name {
  display: inline-block;
  font-family: var(--font-pitch-display) !important;
  font-size: 3.0rem;
  letter-spacing: 0.3rem;
  line-height: 1;
  white-space: nowrap;
}

/* Wrapper that holds its size to the longest word */
.brand-name-2-container {
  font-family: var(--font-pitch-display) !important;
  display: inline-block;
  position: relative;
}

/* Invisible but layout-affecting — sets the stable width */
.brand-name-2-sizer {
  visibility: hidden;
  pointer-events: none;
}

/* Sits on top of sizer, fades in/out */
.brand-name-2-visible {
  position: absolute;
  left: 0;
  top: 0;
}

/* Default: light background */
.tone-primary .brand-name-1 {
  font-family: var(--font-pitch-display) !important;
  color: #1a1a1a; /* near-black */
}
.tone-primary .brand-name-2 {
  font-family: var(--font-pitch-display) !important;
  color: #D4401A; /* primary brand red */
}

/* Dark background variant */
.tone-light .brand-name-1 {
  font-family: var(--font-pitch-display) !important;
  color: #D4401A; /* lightened red for dark bg */
}
.tone-light .brand-name-2 {
  font-family: var(--font-pitch-display) !important;
  color: #ffffff;
}

/* On-primary (red background) variant */
.tone-on-primary .brand-name-1,
.tone-on-primary .brand-name-2 {
  color: #ffffff;
}

/* Hero variant — dark panel: name1 white, name2 brand red */
.tone-hero .brand-name-1 {
  font-family: var(--font-pitch-display) !important;
  color: #ffffff;
}
.tone-hero .brand-name-2 {
  font-family: var(--font-pitch-display) !important;
  color: #D4401A;
}

/* Centered variant — fills parent width and centers content */
.brand-name--centered {
  display: block;
  text-align: center;
}

/* Fit variant — custom name2 (e.g. a memorial name) truncates with an
   ellipsis instead of pushing the header off the edges of the screen */
.brand-name--fit {
  display: inline-flex;
  align-items: baseline;
  flex-direction: row;
  flex-wrap: nowrap;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  /* allow this to shrink below its content width when nested in a flex row,
     otherwise the ellipsis never kicks in and the name runs off the header */
  min-width: 0;
}
/* "Forever" always stays whole — only the memorial name truncates */
.brand-name--fit .brand-name-1 {
  flex: 0 0 auto;
}
.brand-name--fit .brand-name-2 {
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

/* Scale the header down on small screens so more of the name fits */
@media (max-width: 600px) {
  .brand-name {
    font-size: 2rem;
    letter-spacing: 0.15rem;
  }
}

/* Stack variant — on narrow screens, name2 drops below name1 */
@media (max-width: 450px) {
  .brand-name--stack:not(.brand-name--fit) {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
  }
  .brand-name--stack:not(.brand-name--fit).brand-name--centered {
    align-items: center;
  }
  .brand-name--stack:not(.brand-name--fit) .brand-name-2 {
    max-width: 100%;
  }
}

/* Word rotation transition */
.word-fade-enter-active,
.word-fade-leave-active {
  transition: opacity 0.3s ease;
}
.word-fade-enter-from,
.word-fade-leave-to {
  opacity: 0;
}
</style>
